import { CustomPopover } from '../../../../../components/custom-popover';
import { SvgIcon } from '../../../../../components/svg-icon';
import { SvgIconName } from '../../../../../components/svg-icon/model';
import { Button } from 'antd';
import React from 'react';
import './index.scss';
import copy from 'copy-to-clipboard';
import classNames from 'classnames';
import { messageSuccess } from '../../../../../components/message';
import { IAPIGameItem, IAuditItem } from '../../../../../model/api-modules';
import { RbacRoleObjectKey } from '../../../../../model/rbac';
import { GameAvatar } from '../../../common/game-avatar';
import getFormatMessage from '../../../../../locales/get-format-message';
import { PermissionCheck } from '../../../../component/permission-check';
import { auditReadLog } from '../../../../lib/aduit-log';

export interface IGameSettingItemInfo {
  readonly left: React.ReactNode | string;
  readonly right: React.ReactNode | string;
  readonly className?: string;
  readonly copyConfig?: {
    readonly disabled: boolean;
    readonly text: string;
    readonly itemKey?: string;
  };
  readonly editConfig?: {
    readonly disabled: boolean;
    readonly onEdit: () => void;
    readonly editing: boolean;
  };
}

interface IProps {
  readonly items: ReadonlyArray<IGameSettingItemInfo>;
  readonly deleteConfig: {
    readonly description: React.ReactNode | string;
    readonly onDelete: () => void;
    readonly disabled: boolean;
    readonly buttonText: string;
  };
  readonly selectedGame?: IAPIGameItem | undefined;
}

/**
 * @description studio or game setting page layout
 */
export const GameSettingInfoLayout = (props: IProps) => {
  const onCopy = (item: IGameSettingItemInfo) => {
    if (item.copyConfig) {
      copy(item.copyConfig.text);
      if (item.copyConfig.itemKey === 'titleKey') {
        auditReadLog(IAuditItem.CopyTitleKey, {
          gameID: props.selectedGame?.gameID,
          detail: {
            gameID: props.selectedGame?.gameID,
          },
        });
      }
      messageSuccess(getFormatMessage('copy_clipboard'));
    }
  };

  const renderRow = (item: IGameSettingItemInfo, index: number) => {
    return (
      <div className="row-layout webkit-box flex-left" key={index}>
        <div className="row-left">{item.left}</div>
        <div className="row-right webkit-box flex-left">
          {renderRightItem(item)}
        </div>
      </div>
    );
  };

  const renderCopyIcon = (item: IGameSettingItemInfo) => {
    return (
      <SvgIcon
        icon={SvgIconName.Copy}
        className="action-icon pointer m-l"
        onClick={() => onCopy(item)}
      />
    );
  };

  const renderEditIcon = (item: IGameSettingItemInfo) => {
    const EditIcon = (iconProps: { editing: boolean; disabled?: boolean }) => {
      if (iconProps.editing) {
        return null;
      }
      if (iconProps.disabled) {
        return (
          <div className="webkit-box flex-left">
            <SvgIcon
              icon={SvgIconName.StudioNameEdit}
              className="icon-edit edit-disabled pointer m-l"
            />
          </div>
        );
      }
      return (
        <SvgIcon
          icon={SvgIconName.StudioNameEdit}
          className="icon-edit action-icon pointer m-l"
          onClick={item.editConfig?.onEdit}
        />
      );
    };

    if (item.editConfig) {
      return (
        <PermissionCheck permissionKey={RbacRoleObjectKey.GamePut}>
          <EditIcon editing={item.editConfig.editing} />
        </PermissionCheck>
      );
    }
    return null;
  };

  const renderRightItem = (item: IGameSettingItemInfo) => {
    return (
      <>
        <span className={`right-content ${item.className || ''}`}>
          {item.right}
        </span>
        {item.copyConfig ? renderCopyIcon(item) : null}
        {renderEditIcon(item)}
      </>
    );
  };

  const renderDeleteIcon = () => {
    const buttonIcon = (
      <PermissionCheck permissionKey={RbacRoleObjectKey.GameDelete}>
        <Button
          className="delete-button"
          onClick={props.deleteConfig.onDelete}
          type="link"
        >
          {/* <SvgIcon icon={SvgIconName.StudioDelete} /> */}
          <span className="delete-text">{props.deleteConfig.buttonText}</span>
        </Button>
      </PermissionCheck>
    );

    if (props.deleteConfig.disabled) {
      return (
        <CustomPopover
          content={getFormatMessage('access_restricted_desc')}
          trigger="hover"
          placement="topLeft"
        >
          {buttonIcon}
        </CustomPopover>
      );
    }
    return buttonIcon;
  };

  const renderFooterText = () => {
    const deleteClassName = classNames(
      'delete-button-box',
      props.deleteConfig.disabled ? 'disable' : ''
    );
    return (
      <div className="footer-text">
        <div className="text">{props.deleteConfig.description}</div>
        <div className={deleteClassName}>{renderDeleteIcon()}</div>
      </div>
    );
  };

  return (
    <div className="game-setting-info-component">
      <div className="info-layout">
        {props.selectedGame ? (
          <GameAvatar
            width="64px"
            name={props.selectedGame.gameName}
            color={props.selectedGame.gameColor}
            fontSize="40px"
          />
        ) : null}
        <div className="info-box">
          {props.items.map((item, index) => {
            return renderRow(item, index);
          })}
        </div>
      </div>
      {renderFooterText()}
    </div>
  );
};
